import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ROLES, ROLE_PERMISSIONS } from "../mock/fixtures";
import { STORAGE_KEYS } from "../utils/apiClient";
import { USE_MOCK } from "../../api/connection";
import { getMe } from "../../api/services/authService";

const WorkspaceContext = createContext({
  isAuthenticated: false, tenantId: null, role: null, permissions: [], can: () => false
});

function safeGet(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
function safeSet(key, value) { try { localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ } }
function safeRemove(key) { try { localStorage.removeItem(key); } catch (e) { /* storage unavailable */ } }

// No auto-seeding on purpose: a fresh browser (or a signed-out one) has no token, tenant or role
// until a real /api/auth/login call sets them. This context only ever reflects that session.
function readInitial() {
  return {
    token: safeGet(STORAGE_KEYS.token),
    tenantId: safeGet(STORAGE_KEYS.tenant),
    tenantName: safeGet(STORAGE_KEYS.tenantName),
    role: safeGet(STORAGE_KEYS.role),
    userId: safeGet(STORAGE_KEYS.userId),
    userName: safeGet(STORAGE_KEYS.userName),
    userEmail: safeGet(STORAGE_KEYS.userEmail),
    teamRole: safeGet(STORAGE_KEYS.teamRole)
  };
}

export function WorkspaceProvider({ children }) {
  const [initial] = useState(readInitial);
  const [session, setSession] = useState(initial);

  // Storage is written synchronously (not in an effect) so child effects that fire in the same
  // commit already send the new token / X-Tenant-ID / role.
  const applySession = useCallback((payload) => {
    const next = {
      token: payload.token, tenantId: payload.tenantId, tenantName: payload.tenantName || "", role: payload.role,
      userId: payload.userId || "", userName: payload.name || "", userEmail: payload.email || "", teamRole: payload.teamRole || ""
    };
    safeSet(STORAGE_KEYS.token, next.token);
    safeSet(STORAGE_KEYS.tenant, next.tenantId);
    safeSet(STORAGE_KEYS.tenantName, next.tenantName);
    safeSet(STORAGE_KEYS.role, next.role);
    safeSet(STORAGE_KEYS.userId, next.userId);
    safeSet(STORAGE_KEYS.userName, next.userName);
    safeSet(STORAGE_KEYS.userEmail, next.userEmail);
    safeSet(STORAGE_KEYS.teamRole, next.teamRole);
    // Backend mode (JWT): the refresh token apiClient uses to renew the access token.
    if (payload.refreshToken) safeSet(STORAGE_KEYS.refreshToken, payload.refreshToken);
    setSession(next);
  }, []);

  const clearSession = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach(safeRemove);
    setSession({ token: null, tenantId: null, tenantName: null, role: null, userId: null, userName: null, userEmail: null, teamRole: null });
  }, []);

  // Backend mode: on load, confirm the sign-in is still valid and refresh the user and role from
  // GET /api/auth/me (apiClient renews an expired access token first). An ended sign-in fires chatbizz_session_expired.
  useEffect(() => {
    if (USE_MOCK || !initial.token) return undefined;
    let active = true;
    getMe().then((me) => { if (active && me) applySession(me); }).catch(() => { /* handled by apiClient */ });
    return () => { active = false; };
  }, [initial.token, applySession]);

  // apiClient fires this on a 401 SESSION_EXPIRED (token already removed from storage).
  useEffect(() => {
    const onExpired = () => setSession((current) => ({ ...current, token: null }));
    window.addEventListener("chatbizz_session_expired", onExpired);
    return () => window.removeEventListener("chatbizz_session_expired", onExpired);
  }, []);

  const value = useMemo(() => {
    // Production: `permissions` should come from the session/me response, not a local map.
    const permissions = session.role ? (ROLE_PERMISSIONS[session.role] || []) : [];
    return {
      isAuthenticated: !!session.token,
      tenantId: session.tenantId,
      tenant: session.tenantId ? { id: session.tenantId, name: session.tenantName } : null,
      role: session.role,
      roleLabel: (ROLES.find((r) => r.id === session.role) || {}).label,
      teamRole: session.teamRole,
      permissions,
      userId: session.userId,
      userName: session.userName,
      userEmail: session.userEmail,
      can: (permission) => permissions.includes(permission),
      applySession,
      clearSession
    };
  }, [session, applySession, clearSession]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceContext);
